import React, { useState, useEffect } from 'react';
import {
  getScheduledRooms,
  deleteScheduledRoom,
  validateScheduledRoomTime,
  formatTimeUntil,
  buildShareLinks,
} from '../services/roomService';
import { showMeetingBlocked } from '../utils/notices';
import { COLORS, SPACING, BORDER_RADIUS } from '../utils/constants';

export default function ScheduledRoomsList({ onJoinRoom, participantName }) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [copiedKey, setCopiedKey] = useState(null);
  const [deletingRoom, setDeletingRoom] = useState(null);

  // Update current time every second for countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const fetchedRooms = await getScheduledRooms({ upcoming: true });
      setRooms(fetchedRooms);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load scheduled rooms');
      setRooms([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch scheduled rooms
  useEffect(() => {
    fetchRooms();

    // Refresh every 30 seconds
    const interval = setInterval(fetchRooms, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleJoinClick = (room) => {
    // Scheduled Meetings is the organizer/team surface — always join as host/team.
    // Candidates should use the /candidate/ link, not this button.
    const isHost = true;
    const validation = validateScheduledRoomTime(room, currentTime, isHost);

    if (!validation.canJoin) {
      showMeetingBlocked({ ...validation, title: room.title });
      return;
    }

    const joinName = participantName?.trim() || room.hostName;
    if (onJoinRoom) {
      onJoinRoom(room.roomName, joinName, true);
    }
  };

  const handleDeleteRoom = async (room) => {
    if (!room?.roomName || deletingRoom) return;
    const confirmed = window.confirm(
      `Delete “${room.title || room.roomName}”? This removes the MeetGeek room and cancels the linked calendar event.`
    );
    if (!confirmed) return;

    setDeletingRoom(room.roomName);
    try {
      await deleteScheduledRoom(room.roomName, room.hostName, { syncCalendar: true });
      setRooms((prev) => prev.filter((r) => r.roomName !== room.roomName));
    } catch (err) {
      setError(err.message || 'Failed to delete scheduled room');
    } finally {
      setDeletingRoom(null);
    }
  };

  const handleCopyLink = async (roomName, role = 'host') => {
    const links = buildShareLinks(roomName);
    const meetingLink =
      role === 'candidate'
        ? links.candidateLink
        : role === 'team'
          ? links.teamLink
          : links.hostLink;
    const key = `${roomName}:${role}`;
    try {
      await navigator.clipboard.writeText(meetingLink);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      const textArea = document.createElement('textarea');
      textArea.value = meetingLink;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const containerStyle = {
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
  };

  const headerStyle = {
    fontSize: '24px',
    fontWeight: '600',
    color: COLORS.text,
    marginBottom: SPACING.xl,
  };

  const cardStyle = {
    background: COLORS.background,
    borderRadius: BORDER_RADIUS.lg,
    padding: SPACING.xl,
    marginBottom: SPACING.lg,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    border: `1px solid ${COLORS.border}`,
  };

  const titleStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: COLORS.text,
    marginBottom: SPACING.sm,
  };

  const descriptionStyle = {
    fontSize: '14px',
    color: COLORS.textSecondary,
    marginBottom: SPACING.md,
  };

  const infoRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.md,
    marginBottom: SPACING.sm,
    fontSize: '14px',
    color: COLORS.textSecondary,
  };

  const statusBadgeStyle = (status) => ({
    display: 'inline-block',
    padding: `${SPACING.xs} ${SPACING.md}`,
    borderRadius: BORDER_RADIUS.pill,
    fontSize: '12px',
    fontWeight: '500',
    ...(status === 'upcoming' && {
      background: '#E3F2FD',
      color: '#1976D2',
    }),
    ...(status === 'live' && {
      background: '#E8F5E9',
      color: '#388E3C',
    }),
    ...(status === 'ended' && {
      background: '#FFEBEE',
      color: '#D32F2F',
    }),
  });

  const buttonStyle = (canJoin) => ({
    padding: `${SPACING.md} ${SPACING.xl}`,
    background: canJoin ? COLORS.primaryGradient : COLORS.border,
    color: canJoin ? COLORS.background : COLORS.textSecondary,
    border: 'none',
    borderRadius: BORDER_RADIUS.md,
    fontSize: '14px',
    fontWeight: '500',
    cursor: canJoin ? 'pointer' : 'not-allowed',
    transition: 'all 0.2s ease',
    marginTop: SPACING.md,
  });

  const copyButtonStyle = (isCopied) => ({
    padding: `${SPACING.xs} ${SPACING.md}`,
    background: isCopied ? COLORS.success : 'transparent',
    color: isCopied ? COLORS.background : COLORS.primary,
    border: `1px solid ${COLORS.primary}`,
    borderRadius: BORDER_RADIUS.md,
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
  });

  const deleteButtonStyle = (isDeleting) => ({
    padding: `${SPACING.xs} ${SPACING.md}`,
    background: isDeleting ? '#fee2e2' : 'transparent',
    color: COLORS.error || '#d32f2f',
    border: `1px solid ${COLORS.error || '#d32f2f'}`,
    borderRadius: BORDER_RADIUS.md,
    fontSize: '12px',
    fontWeight: '500',
    cursor: isDeleting ? 'wait' : 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
    opacity: isDeleting ? 0.7 : 1,
  });

  const countdownStyle = {
    fontSize: '16px',
    fontWeight: '600',
    color: COLORS.primary,
    marginTop: SPACING.sm,
  };

  const loadingStyle = {
    textAlign: 'center',
    color: COLORS.textSecondary,
    padding: SPACING.xxl,
  };

  const errorStyle = {
    textAlign: 'center',
    color: COLORS.error,
    padding: SPACING.xxl,
  };

  const emptyStyle = {
    textAlign: 'center',
    color: COLORS.textSecondary,
    padding: SPACING.xxl,
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <h2 style={headerStyle}>Scheduled Meetings</h2>
        <div style={loadingStyle}>Loading scheduled rooms...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <h2 style={headerStyle}>Scheduled Meetings</h2>
        <div style={errorStyle}>{error}</div>
      </div>
    );
  }

  if (rooms.length === 0) {
    return (
      <div style={containerStyle}>
        <h2 style={headerStyle}>Scheduled Meetings</h2>
        <div style={emptyStyle}>No scheduled meetings found</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <h2 style={headerStyle}>Scheduled Meetings</h2>
      {rooms.map((room) => {
        // List joiners are team/hosts; candidates use /candidate links.
        const validation = validateScheduledRoomTime(room, currentTime, true);
        const startTime = new Date(room.scheduledStartTime);
        const endTime = new Date(room.scheduledEndTime);
        const timeUntilStart = validation.timeUntilStart || 0;

        return (
          <div key={room.roomName} style={cardStyle}>
            <div style={titleStyle}>{room.title}</div>
            {room.description && <div style={descriptionStyle}>{room.description}</div>}
            
            <div style={{ ...infoRowStyle, justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.md }}>
                <span>👤 Host: {room.hostName}</span>
                <span style={statusBadgeStyle(room.status)}>{room.status.toUpperCase()}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  style={copyButtonStyle(copiedKey === `${room.roomName}:host`)}
                  onClick={() => handleCopyLink(room.roomName, 'host')}
                  title={buildShareLinks(room.roomName).hostLink}
                >
                  {copiedKey === `${room.roomName}:host` ? '✓ Host link' : 'Copy host link'}
                </button>
                <button
                  type="button"
                  style={copyButtonStyle(copiedKey === `${room.roomName}:team`)}
                  onClick={() => handleCopyLink(room.roomName, 'team')}
                  title={buildShareLinks(room.roomName).teamLink}
                >
                  {copiedKey === `${room.roomName}:team` ? '✓ Team link' : 'Copy team link'}
                </button>
                <button
                  type="button"
                  style={copyButtonStyle(copiedKey === `${room.roomName}:candidate`)}
                  onClick={() => handleCopyLink(room.roomName, 'candidate')}
                  title={buildShareLinks(room.roomName).candidateLink}
                >
                  {copiedKey === `${room.roomName}:candidate` ? '✓ Candidate link' : 'Copy candidate link'}
                </button>
                <button
                  type="button"
                  style={deleteButtonStyle(deletingRoom === room.roomName)}
                  onClick={() => handleDeleteRoom(room)}
                  disabled={deletingRoom === room.roomName}
                  title="Delete meeting"
                >
                  {deletingRoom === room.roomName ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>

            <div style={infoRowStyle}>
              <span>⏰ Starts: {startTime.toLocaleString()}</span>
            </div>

            <div style={infoRowStyle}>
              <span>⏱️ Ends: {endTime.toLocaleString()}</span>
            </div>

            {!validation.canJoin && validation.reason === 'upcoming' && (
              <div style={countdownStyle}>
                ⏳ Room starts in: {formatTimeUntil(timeUntilStart)}
              </div>
            )}

            {!validation.canJoin && validation.reason === 'ended' && (
              <div style={{ ...countdownStyle, color: COLORS.error }}>
                ❌ This room has ended
              </div>
            )}

            <button
              style={buttonStyle(validation.canJoin)}
              onClick={() => handleJoinClick(room)}
              disabled={!validation.canJoin}
              onMouseEnter={(e) => {
                if (validation.canJoin) {
                  e.target.style.opacity = '0.9';
                }
              }}
              onMouseLeave={(e) => {
                if (validation.canJoin) {
                  e.target.style.opacity = '1';
                }
              }}
            >
              {validation.canJoin ? 'Join Meeting' : validation.message}
            </button>
          </div>
        );
      })}
    </div>
  );
}

